"use client";

import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth";
import ChatMessage, { ActionCard, TypingIndicator } from "./ChatMessage";
import { recallConversation } from "@/lib/amandaConversation";
import { useAmandaChat, type Conversation, type UploadedFile } from "@/hooks/useAmandaChat";

// Custom event for proactive chat triggers
// Usage: window.dispatchEvent(new CustomEvent("sf:chat-trigger", { detail: { message: "...", autoSend: true } }))
interface ChatTriggerEvent extends CustomEvent {
  detail: { message: string; autoSend?: boolean };
}

/**
 * The floating Amanda widget — a SHELL (FAB, panel, history drawer, upload
 * affordance, quota nudge) over the one chat engine, `useAmandaChat`, which
 * the editor panel also uses (spec §10: one engine, two shells). Everything
 * that talks to the API or parses the SSE stream lives in the hook.
 */
export default function ChatWidget() {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  // Hide floating FAB on the edit page (it has its own embedded Amanda panel)
  const isEditPage = pathname?.includes("/edit");

  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState("");
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);

  const {
    messages,
    actions,
    isStreaming,
    statusText,
    isLoading,
    quota,
    error,
    conversations,
    isUploading,
    activeConversationId,
    sendMessage,
    stopStreaming,
    newConversation,
    resumeConversation,
    selectConversation,
    deleteConversation,
    clearAllConversations,
    loadQuota,
    loadConversations,
    loadMessages,
    uploadFile,
  } = useAmandaChat({
    // The engine has already re-homed the URL to this tab's workspace.
    onNavigateTo: (url) => router.push(url),
  });

  // Draggable FAB position — persisted in localStorage
  const [fabPos, setFabPos] = useState<{ x: number; y: number } | null>(null);
  const fabDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; moved: boolean } | null>(null);
  const fabJustDragged = useRef(false);
  const [fabDragging, setFabDragging] = useState(false);

  // Load persisted FAB position
  useEffect(() => {
    try {
      const saved = localStorage.getItem("sf_amanda_pos");
      if (saved) {
        const pos = JSON.parse(saved);
        if (typeof pos.x === "number" && typeof pos.y === "number") {
          // Clamp to current viewport
          const maxX = window.innerWidth - 60;
          const maxY = window.innerHeight - 60;
          setFabPos({ x: Math.min(Math.max(8, pos.x), maxX), y: Math.min(Math.max(8, pos.y), maxY) });
        }
      }
    } catch { /* ignore */ }
  }, []);

  const handleFabDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const coords = "touches" in e
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY };

    // Use current position or default (bottom-right)
    const currentX = fabPos?.x ?? window.innerWidth - 60;
    const currentY = fabPos?.y ?? window.innerHeight - 60;

    fabDragRef.current = { startX: coords.x, startY: coords.y, origX: currentX, origY: currentY, moved: false };

    const handleMove = (ev: MouseEvent | TouchEvent) => {
      if (!fabDragRef.current) return;
      const c = "touches" in ev
        ? { x: (ev as TouchEvent).touches[0].clientX, y: (ev as TouchEvent).touches[0].clientY }
        : { x: (ev as MouseEvent).clientX, y: (ev as MouseEvent).clientY };

      const dist = Math.abs(c.x - fabDragRef.current.startX) + Math.abs(c.y - fabDragRef.current.startY);
      if (dist < 5 && !fabDragRef.current.moved) return; // Dead zone

      if (ev.cancelable) ev.preventDefault();
      fabDragRef.current.moved = true;
      setFabDragging(true);

      const newX = Math.min(Math.max(8, fabDragRef.current.origX + (c.x - fabDragRef.current.startX)), window.innerWidth - 60);
      const newY = Math.min(Math.max(8, fabDragRef.current.origY + (c.y - fabDragRef.current.startY)), window.innerHeight - 60);
      const pos = { x: newX, y: newY };
      setFabPos(pos);
      // Save immediately so handleEnd closure has latest
      localStorage.setItem("sf_amanda_pos", JSON.stringify(pos));
    };

    const handleEnd = () => {
      if (fabDragRef.current?.moved) {
        fabJustDragged.current = true;
        // Persist position — read latest from state via closure
        setTimeout(() => { fabJustDragged.current = false; }, 100);
      }
      fabDragRef.current = null;
      setFabDragging(false);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
  }, [fabPos]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingAutoSendRef = useRef<string | null>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, actions, isStreaming]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Load quota + auto-resume conversation when opened
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadQuota();
      if (activeConversationId && messages.length === 0) {
        loadMessages(activeConversationId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAuthenticated]);

  // Auto-send when a suggested question is clicked
  useEffect(() => {
    if (pendingAutoSendRef.current && input === pendingAutoSendRef.current) {
      pendingAutoSendRef.current = null;
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Listen for proactive chat triggers from other components
  useEffect(() => {
    function handleTrigger(e: Event) {
      const event = e as ChatTriggerEvent;
      const msg = event.detail?.message;
      if (!isAuthenticated) return;

      // Open widget (and optionally prefill or auto-send message)
      setIsOpen(true);
      if (msg) {
        setInput(msg);
        if (event.detail?.autoSend) {
          // Continue the conversation this browser used in the last 30 minutes
          // (a reload of the New Envelope page must not orphan the draft Amanda
          // just created); otherwise start fresh so a stale id never leaks in.
          resumeConversation(recallConversation());
          pendingAutoSendRef.current = msg;
        }
      }

      // Mark that user has chatted (for dashboard card visibility)
      localStorage.setItem("sf_has_chatted", "true");

      // Track analytics
      if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
        (window as any).sfAnalytics.track("chat_trigger_fired", "Chat", msg ? msg.slice(0, 50) : "banner_open");
      }
    }

    window.addEventListener("sf:chat-trigger", handleTrigger);
    return () => window.removeEventListener("sf:chat-trigger", handleTrigger);
  }, [isAuthenticated, resumeConversation]);

  async function handleSend() {
    const content = input.trim();
    if (!content || isStreaming) return;
    const fileMeta = uploadedFile ?? undefined;
    setInput("");
    setUploadedFile(null);
    await sendMessage(content, fileMeta);
  }

  function handleStop() {
    stopStreaming();
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const meta = await uploadFile(file);
    if (meta) {
      setUploadedFile(meta);
      // Auto-populate a message about the upload
      setInput(`I've uploaded ${meta.filename}. Can you analyze it and set it up for signing?`);
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleNewConversation() {
    newConversation();
    setShowHistory(false);
  }

  async function handleSelectConversation(conv: Conversation) {
    setShowHistory(false);
    await selectConversation(conv.id);
  }

  function handleToggleHistory() {
    if (!showHistory) {
      loadConversations();
    }
    setShowHistory(!showHistory);
  }

  async function handleDeleteConversation(e: React.MouseEvent, convId: string) {
    e.stopPropagation();
    await deleteConversation(convId);
  }

  async function handleClearAll() {
    if (await clearAllConversations()) {
      setShowHistory(false);
    }
  }

  if (!isAuthenticated) return null;
  if (isEditPage) return null;

  return (
    <div
      ref={widgetRef}
      className="fixed z-[45] flex flex-col items-end gap-2"
      style={fabPos && !isOpen
        ? { left: fabPos.x, top: fabPos.y, right: "auto", bottom: "auto" }
        : { bottom: 20, right: 20 }
      }
    >
      {/* ─── Expanded Panel ─── */}
      {isOpen && (
        <div
          className="w-[380px] max-w-[calc(100vw-40px)] h-[600px] max-h-[min(600px,80vh)] rounded-2xl border border-slate-200 bg-slate-50 flex flex-col overflow-hidden"
          style={{
            animation: "chatSlideUp 0.25s ease-out",
            boxShadow: "0 1px 1px hsl(220 60% 50% / 0.06), 0 2px 4px hsl(220 60% 50% / 0.06), 0 4px 8px hsl(220 60% 50% / 0.06), 0 8px 16px hsl(220 60% 50% / 0.06), 0 16px 32px hsl(220 60% 50% / 0.06)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-sm">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-slate-900 leading-tight">Amanda AI</p>
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 leading-none">Beta</span>
                </div>
                <p className="text-[10px] text-slate-400">SignForge support assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleToggleHistory}
                title="Conversation history"
                className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={handleNewConversation}
                title="New conversation"
                className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="absolute inset-0 top-[52px] bg-white z-10 flex flex-col overflow-hidden rounded-b-2xl">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">Conversations</p>
                  <div className="flex items-center gap-2">
                    {conversations.length > 0 && (
                      <button
                        onClick={handleClearAll}
                        className="text-[10px] text-red-500 hover:text-red-700 font-medium"
                      >
                        Clear all
                      </button>
                    )}
                    <button
                      onClick={() => setShowHistory(false)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No conversations yet</p>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`group flex items-center border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                        conv.id === activeConversationId ? "bg-brand-50" : ""
                      }`}
                    >
                      <button
                        onClick={() => handleSelectConversation(conv)}
                        className="flex-1 text-left px-4 py-3 min-w-0"
                      >
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {conv.title || "New conversation"}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-slate-400">{conv.message_count} messages</span>
                          {conv.status === "escalated" && (
                            <span className="text-[10px] text-amber-600 font-medium">Escalated</span>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        title="Delete conversation"
                        className="shrink-0 mr-3 p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center mb-3">
                  <svg className="h-6 w-6 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <p className="text-xs font-medium text-slate-700 mb-1">Hi, I&apos;m Amanda!</p>
                <p className="text-[10px] text-slate-400 max-w-[220px]">Your SignForge assistant. Ask me about features, plans, troubleshooting, or anything else.</p>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  {["What plans do you offer?", "How do I send a document?", "I need help with signing", "Report a bug or feedback"].map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); pendingAutoSendRef.current = q; }}
                      className="text-[10px] px-2 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-white hover:border-slate-300 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <svg className="h-5 w-5 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                  <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                </svg>
              </div>
            )}

            {/* Messages with per-message action cards */}
            {messages.map((msg) => (
              <Fragment key={msg.id}>
                <ChatMessage role={msg.role} content={msg.content} />
                {msg.actions?.map((a) => (
                  <ActionCard key={a.id} action={a.action} data={a.data} />
                ))}
              </Fragment>
            ))}
            {/* Live streaming actions (not yet saved to DB) */}
            {actions.map((a) => (
              <ActionCard key={a.id} action={a.action} data={a.data} isLive={isStreaming} />
            ))}
            {isStreaming && !(messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.content?.trim()) && <TypingIndicator label={statusText} />}

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-200 bg-white px-3 py-3">
            {/* Uploaded file indicator */}
            {uploadedFile && (
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="flex items-center gap-1.5 rounded-lg bg-brand-50 border border-brand-200 px-2.5 py-1.5 text-xs text-brand-700">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <span className="font-medium truncate max-w-[200px]">{uploadedFile.filename}</span>
                  <button onClick={() => setUploadedFile(null)} className="text-slate-400 hover:text-slate-600">
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-end gap-2">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              {/* Paperclip / upload button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isStreaming || isUploading}
                title="Upload a PDF"
                className="flex-shrink-0 h-10 w-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 flex items-center justify-center hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                )}
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isStreaming ? "Click stop to interrupt..." : uploadedFile ? "Ask Amanda about your PDF..." : "Type a message..."}
                disabled={false}
                rows={1}
                className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 disabled:opacity-50 transition-all max-h-24"
                style={{ minHeight: "40px" }}
              />
              {isStreaming ? (
                <button
                  onClick={handleStop}
                  title="Stop generating"
                  className="flex-shrink-0 h-10 w-10 rounded-xl bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="flex-shrink-0 h-10 w-10 rounded-xl bg-brand-600 text-white flex items-center justify-center hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                </button>
              )}
            </div>
            {quota && quota.limit !== null && quota.remaining !== null && quota.remaining <= 10 && (
              <p className={`text-[10px] mt-1.5 text-center ${quota.remaining <= 3 ? "text-red-500 font-medium" : "text-amber-600"}`}>
                {quota.remaining <= 0
                  ? "Message limit reached — upgrade for more"
                  : `${quota.remaining} message${quota.remaining === 1 ? "" : "s"} left this month`}
              </p>
            )}
            <p className="text-[9px] text-slate-400 text-center mt-1">AI can make mistakes. Please review all changes.</p>
          </div>
        </div>
      )}

      {/* ─── Collapsed Button (draggable) ─── */}
      <button
        onMouseDown={(e) => { if (!isOpen) handleFabDragStart(e); }}
        onTouchStart={(e) => { if (!isOpen) handleFabDragStart(e); }}
        onClick={() => {
          // Don't toggle if we just finished dragging
          if (fabJustDragged.current) return;
          setIsOpen(!isOpen);
          if (!isOpen) {
            localStorage.setItem("sf_has_chatted", "true");
            if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
              (window as any).sfAnalytics.track("chat_opened", "Chat");
            }
          }
          // Reset position when opening (panel anchors to bottom-right)
          if (!isOpen && fabPos) {
            setFabPos(null);
            localStorage.removeItem("sf_amanda_pos");
          }
        }}
        className={`group flex items-center gap-2 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl touch-none ${
          isOpen
            ? "bg-slate-700 text-white hover:bg-slate-800 px-4 py-2.5"
            : `bg-white text-slate-700 border border-slate-200 hover:border-slate-300 px-2.5 py-2.5 lg:px-4 lg:py-2.5 ${fabDragging ? "cursor-grabbing scale-110 shadow-2xl" : "cursor-grab"}`
        }`}
      >
        {isOpen ? (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <>
            <svg className="h-5 w-5 lg:h-4 lg:w-4 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <span className="text-sm font-medium hidden lg:inline">Amanda AI</span>
            <span className="hidden lg:inline-flex items-center rounded-full bg-amber-100/80 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-amber-700 leading-none">Beta</span>
          </>
        )}
      </button>

      {/* Animation */}
      <style jsx>{`
        @keyframes chatSlideUp {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
